import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Translate, translate } from 'react-jhipster';
import { Alert, Button } from 'reactstrap';
import OTPInput from 'react-otp-input';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import { loginWithToken } from 'app/shared/reducers/authentication';
import {
  getTransferStatus,
  requestTransferOtp,
  verifyTransferOtp,
  resetTransferOtp,
  resetOtpError,
  resetVerifyError,
} from './transfer-otp.reducer';

const RESEND_COOLDOWN_SECONDS = 60;

interface ITransferOtpProps {
  quotationId: string;
  // Where bank staff go to log in with a password instead; carries the bought page as `from`
  staffLogin: { pathname: string; state: any };
}

/**
 * OTP screen shown in place of the quotation bought page when nobody is logged in (ALSHMOKH-34).
 * Once the code is verified the session is stored and PrivateRoute renders the bought page on the same URL.
 */
export const TransferOtp = ({ quotationId, staffLogin }: ITransferOtpProps) => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(state => state.transferOtp.loading);
  const status = useAppSelector(state => state.transferOtp.status);
  const ecertNo = useAppSelector(state => state.transferOtp.ecertNo);
  const maskedPhone = useAppSelector(state => state.transferOtp.maskedPhone);
  const sendingOtp = useAppSelector(state => state.transferOtp.sendingOtp);
  const otpSent = useAppSelector(state => state.transferOtp.otpSent);
  const otpError = useAppSelector(state => state.transferOtp.otpError);
  const verifying = useAppSelector(state => state.transferOtp.verifying);
  const verifyError = useAppSelector(state => state.transferOtp.verifyError);
  const verifySuccess = useAppSelector(state => state.transferOtp.verifySuccess);
  const idToken = useAppSelector(state => state.transferOtp.idToken);

  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const autoSentRef = useRef(false);

  const handleSendCode = () => {
    dispatch(resetOtpError());
    dispatch(requestTransferOtp(quotationId)).then(result => {
      if (!('error' in result) || !result.error) {
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    });
  };

  useEffect(() => {
    autoSentRef.current = false;
    dispatch(resetTransferOtp());
    dispatch(getTransferStatus(quotationId));
  }, [quotationId]);

  // Opening the page sends the first code automatically; afterwards only the 60s-gated resend is offered.
  useEffect(() => {
    if (status === 'ACTIVE' && !autoSentRef.current) {
      autoSentRef.current = true;
      handleSendCode();
    }
  }, [status]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setInterval(() => setCooldown(previous => previous - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (verifySuccess && idToken) {
      dispatch(loginWithToken(idToken));
    }
  }, [verifySuccess, idToken]);

  const handleVerify = () => {
    dispatch(resetVerifyError());
    dispatch(verifyTransferOtp({ quotationId, code }));
  };

  const staffLoginLink = (
    <div className="mt-4">
      <Link to={staffLogin}>
        <Translate contentKey="transferOtp.staffLogin">Bank staff? Log in here</Translate>
      </Link>
    </div>
  );

  if (loading || verifySuccess) {
    return (
      <div className="transfer-otp-page text-center mt-5">
        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
      </div>
    );
  }

  if (status === 'INVALID') {
    return (
      <div className="transfer-otp-page text-center mt-5">
        <Alert color="danger">
          <Translate contentKey="transferOtp.invalidLink">This link is invalid or has expired.</Translate>
        </Alert>
        {staffLoginLink}
      </div>
    );
  }

  if (status === 'COMPLETED') {
    return (
      <div className="transfer-otp-page text-center mt-5">
        <Alert color="info">
          <Translate contentKey="transferOtp.completed">This transaction has already been completed.</Translate>
        </Alert>
        {staffLoginLink}
      </div>
    );
  }

  return (
    <div className="transfer-otp-page text-center mt-5">
      <h3>
        <Translate contentKey="transferOtp.title">Verify your identity</Translate>
      </h3>
      {ecertNo && (
        <p>
          <Translate contentKey="transferOtp.ecertNo" interpolate={{ ecertNo }}>
            Transaction no. {ecertNo}
          </Translate>
        </p>
      )}

      {!otpSent ? (
        <>
          <p>
            <Translate contentKey="transferOtp.subtitle" interpolate={{ maskedPhone }}>
              We will send a verification code to {maskedPhone}
            </Translate>
          </p>
          {otpError && (
            <Alert color="danger">
              <Translate contentKey={otpError}>Could not send the code. Please try again.</Translate>
            </Alert>
          )}
          <Button color="primary" onClick={handleSendCode} disabled={sendingOtp}>
            {sendingOtp && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
            <Translate contentKey="transferOtp.sendCode">Send code</Translate>
          </Button>
        </>
      ) : (
        <>
          <p>
            <Translate contentKey="transferOtp.enterCode" interpolate={{ maskedPhone }}>
              Enter the 6-digit code sent to {maskedPhone}
            </Translate>
          </p>
          {verifyError && (
            <Alert color="danger">
              <Translate contentKey={verifyError}>Incorrect code. Please try again.</Translate>
            </Alert>
          )}
          <div className="d-flex justify-content-center" dir="ltr">
            <OTPInput
              value={code}
              onChange={value => setCode(value)}
              numInputs={6}
              renderSeparator={<span style={{ padding: '0 8px' }} />}
              renderInput={props => <input {...props} />}
              inputStyle={{ width: '40px', height: '48px' }}
              inputType="tel"
            />
          </div>
          <div className="mt-3">
            <Button color="primary" onClick={handleVerify} disabled={verifying || code.length < 6}>
              {verifying && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
              <Translate contentKey="transferOtp.verify">Verify</Translate>
            </Button>
          </div>
          <div className="mt-3">
            {cooldown > 0 ? (
              <span>{translate('transferOtp.resendIn', { seconds: cooldown })}</span>
            ) : (
              <Button color="link" onClick={handleSendCode} disabled={sendingOtp}>
                <Translate contentKey="transferOtp.resend">Send a new code</Translate>
              </Button>
            )}
          </div>
        </>
      )}
      {staffLoginLink}
    </div>
  );
};

export default TransferOtp;
